import { useMemo } from 'react'

import { env } from '@/config/env'
import { CompareMapsContextProvider } from '@/contexts/compare-maps-context'
import { useCompanies } from '@/hooks/use-companies'
import { Activities } from '@/pages/activities'
import DemandAnalysis from '@/pages/demand-analysis'
import Demands from '@/pages/demands'
import Diagnoses from '@/pages/diagnoses'
import EditDiagnosis from '@/pages/diagnosis-edit'
import DiagnosisView from '@/pages/diagnosis-view'
import Folders from '@/pages/folders'
import Maps from '@/pages/maps'
import Members from '@/pages/members'
import { NewActivity } from '@/pages/new-activity'
import Projects from '@/pages/projects'
import ProjectsForm from '@/pages/projects-form'
import Reports from '@/pages/reports'
import { SavePulverizationActivity } from '@/pages/save-pulverization-activity'
import Teams from '@/pages/teams'
import WeedDiagnoses from '@/pages/weed-diagnoses'
import { routes } from '@/routes/config'

import DiagnosesCompare from '../pages/diagnoses-compare'

interface IPrivateRoute {
  path: string
  to?: string
  Component: React.FC<any>
  name?: string
}

interface IPrivateRouteGroup {
  Wrapper: React.FC
  routes: IPrivateRoute[]
  name?: string
}

export type PrivateRouteConfig = IPrivateRoute | IPrivateRouteGroup

const commonsRoutes: IPrivateRoute[] = [
  {
    path: routes.Demands(),
    to: routes.Demands('register'),
    Component: Demands,
  },
  {
    path: routes.EditDiagnosis(),
    to: routes.EditDiagnosis('editname'),
    Component: EditDiagnosis,
  },
  { path: routes.DemandAnalysis(), Component: DemandAnalysis },
  { path: routes.DiagnosisView(), Component: DiagnosisView },
  { path: routes.Projects(), Component: Projects, name: 'Projetos' },
  { path: routes.ProjectAdd(), Component: ProjectsForm },
  { path: routes.ProjectEdit(), Component: ProjectsForm },
  { path: routes.Members(), Component: Members },
]

const physiologicalPerformanceRoutes: PrivateRouteConfig[] = [
  { path: routes.Diagnoses(), Component: Diagnoses, name: 'Diagnósticos' },
  ...commonsRoutes,
  env.enablePreviousFeatures && {
    path: routes.Teams(),
    Component: Teams,
    name: 'Equipe',
  },
  env.enablePreviousFeatures && {
    path: routes.Folders(),
    Component: Folders,
    name: 'Pastas',
  },
].filter(Boolean) as IPrivateRoute[]

const weedRoutes: PrivateRouteConfig[] = [
  { path: routes.Diagnoses(), Component: WeedDiagnoses, name: 'Diagnósticos' },
  {
    Wrapper: CompareMapsContextProvider,
    routes: [
      { path: routes.Maps(), Component: Maps, name: 'Mapas' },
      { path: routes.DiagnosesCompare(), Component: DiagnosesCompare },
    ],
  },
  { path: routes.Activities(), Component: Activities, name: 'Calendários' },
  { path: routes.ActivityAdd(), Component: NewActivity },
  {
    path: routes.ConfirmPulverizationActivity(),
    Component: SavePulverizationActivity,
  },
  {
    path: routes.Reports(),
    to: routes.Reports('economy-indicators'),
    Component: Reports,
    name: 'Relatórios',
  },
  ...commonsRoutes,
]

export const usePrivateRoutes = () => {
  const { permissions, isCompaniesError, isCompaniesLoading } = useCompanies()

  const privateRoutes = useMemo(() => {
    if (isCompaniesError || isCompaniesLoading) return

    return permissions?.WeedAccess ? weedRoutes : physiologicalPerformanceRoutes
  }, [permissions?.WeedAccess, isCompaniesLoading, isCompaniesError])

  return privateRoutes
}
